import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconNames } from '@dotcom/crown-ui';
import { useAppContext } from '../../../context/app-context';
import { useCardContext } from '../../../context/card-context';
import {
  captureCanvasSnapshot,
  useEditHistoryCoordinator,
  useEditHistorySessionKey,
  useResolveEditHistoryFaceIdentity,
} from '../../../features/edit-history/edit-history';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useUIStore } from '../../../stores/ui';
import { useFontDrawerFlags } from '../font-drawer/hooks/use-font-drawer-flags';
import { handleTextTabClick } from '../font-drawer/utils/handle-text-tab-click';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { ToolbarAction, ToolbarActionMenu, useToolbarExitAnimation, useToolbarPosition } from '../toolbar-action-menu';
import { FontInlineTools } from './fragments/font-inline-tools';
import { useCachedActiveText } from './hooks/use-cached-active-text';
import { getFontToolbarHost } from './font-toolbar-host';
import { FontToolbarInlineTool } from './font-toolbar-types';
import styles from './font-toolbar.module.scss';

const TOOLBAR_TEST_ID_PREFIX = 'font-toolbar';

const translateOrFallback = (value: unknown, fallback: string): string =>
  typeof value === 'string' ? value : fallback;

export const FontToolbar = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const {
    appState: { isMobileApp },
  } = useAppContext();
  const { cardState } = useCardContext();
  const activeCardIndex = cardState.activeCardIndex;
  const canvas = useActiveCanvas();
  const historyCoordinator = useEditHistoryCoordinator();
  const editorSessionKey = useEditHistorySessionKey();
  const resolveHistoryFace = useResolveEditHistoryFaceIdentity();
  const activeCanvas = canvas?.current;
  const historyFace = useMemo(
    () => (activeCanvas ? resolveHistoryFace?.(activeCanvas) : null),
    [activeCanvas, resolveHistoryFace],
  );
  const historySnapshotRef = useRef<ReturnType<typeof captureCanvasSnapshot> | null>(null);
  const { shouldShowColorEditor } = useFontDrawerFlags();

  const isFontToolbarOpen = useUIStore((state) => state.isFontToolbarOpen);
  const activeTool = useUIStore((state) => state.fontToolbarActiveTool) as FontToolbarInlineTool | null;
  const closeFontToolbar = useUIStore((state) => state.closeFontToolbar);
  const setActiveTool = useUIStore((state) => state.setFontToolbarActiveTool);

  const isMobile = Boolean(isMobileApp);
  const isVisible = isFontToolbarOpen || activeTool !== null;

  useEffect(() => {
    if (!isVisible || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      if (!isVisible) {
        historySnapshotRef.current = null;
      }
      return;
    }
    try {
      const targetCanvas = canvas.current;
      const beforeSnapshot = captureCanvasSnapshot(targetCanvas);
      historySnapshotRef.current = beforeSnapshot;
      const unregisterPending = historyCoordinator.registerPendingTransaction(
        { editorSessionKey, faceId: historyFace.faceId, faceNumber: historyFace.faceNumber, type: historyFace.type },
        targetCanvas,
        {
          hasPendingChanges: () => false,
          getBeforeSnapshot: () => historySnapshotRef.current,
          rebase: (snapshot) => {
            historySnapshotRef.current = snapshot;
          },
        },
      );
      return () => {
        unregisterPending();
        if (historySnapshotRef.current !== beforeSnapshot) {
          return;
        }
        historySnapshotRef.current = null;
        const currentFace = resolveHistoryFace?.(targetCanvas);
        if (
          currentFace?.editorSessionKey !== editorSessionKey ||
          currentFace?.faceId !== historyFace.faceId ||
          currentFace?.faceNumber !== historyFace.faceNumber ||
          currentFace?.type !== historyFace.type
        ) {
          return;
        }
        void historyCoordinator
          .commitCapturedTransaction(
            {
              editorSessionKey,
              faceId: historyFace.faceId,
              faceNumber: historyFace.faceNumber,
              type: historyFace.type,
            },
            targetCanvas,
            beforeSnapshot,
          )
          .catch(() => undefined);
      };
    } catch {
      historySnapshotRef.current = null;
    }
  }, [isVisible, historyCoordinator, editorSessionKey, historyFace, canvas, resolveHistoryFace]);

  // Desktop exit animation — keeps the panel mounted while the slide-out plays.
  const {
    isRendered: isDesktopRendered,
    isClosing: isDesktopClosing,
    onExitComplete: onDesktopExitComplete,
  } = useToolbarExitAnimation(!isMobile && isVisible);

  // Align toolbar top with the canvas wrapper's top edge.
  // Sets --toolbar-anchor-top on the host — CSS handles the rest.
  useToolbarPosition({
    getHost: getFontToolbarHost,
    getAnchor: () => canvas?.current?.getElement()?.closest<HTMLElement>('[data-testid^="canvas-wrapper-"]') ?? null,
    enabled: !isMobile && isVisible,
    measurementKey: activeCardIndex,
  });

  // Cache the most recently selected text object so inline tools can still
  // act on a valid target when canvas focus is momentarily lost.
  useCachedActiveText({ canvas, enabled: isVisible });

  // Reset active tool when toolbar fully closes.
  useEffect(() => {
    if (!isVisible) {
      setActiveTool(null);
    }
  }, [isVisible, setActiveTool]);

  const handleClose = useCallback(() => {
    const beforeSnapshot = historySnapshotRef.current;
    historySnapshotRef.current = null;
    const closeToolbar = () => {
      setActiveTool(null);
      closeFontToolbar();
      canvas?.current?.discardActiveObject();
      canvas?.current?.requestRenderAll();
    };

    if (!beforeSnapshot || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      closeToolbar();
      return;
    }

    const currentFace = resolveHistoryFace?.(canvas.current);
    if (
      currentFace?.editorSessionKey !== editorSessionKey ||
      currentFace?.faceId !== historyFace.faceId ||
      currentFace?.faceNumber !== historyFace.faceNumber ||
      currentFace?.type !== historyFace.type
    ) {
      closeToolbar();
      return;
    }

    void historyCoordinator
      .commitCapturedTransaction(
        {
          editorSessionKey,
          faceId: historyFace.faceId,
          faceNumber: historyFace.faceNumber,
          type: historyFace.type,
        },
        canvas.current,
        beforeSnapshot,
      )
      .catch(() => undefined)
      .finally(closeToolbar);
  }, [canvas, closeFontToolbar, setActiveTool, historyCoordinator, editorSessionKey, historyFace, resolveHistoryFace]);

  // Close the toolbar (from any state) when the user presses Escape.
  // Provides a direct-close affordance in sub-panels where the X is hidden,
  // and matches the universal modal/menu dismissal shortcut.
  // Must clear `activeTool` too, otherwise `isVisible` (which OR-includes
  // `activeTool !== null`) would keep the panel mounted.
  useEffect(() => {
    if (!isVisible) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleClose]);

  const openInlineTool = useCallback(
    (tool: FontToolbarInlineTool) => {
      setActiveTool(tool);
    },
    [setActiveTool],
  );

  const handleBack = useCallback(() => {
    if (activeTool) {
      setActiveTool(null);
      return;
    }
    handleClose();
  }, [activeTool, handleClose, setActiveTool]);

  const labels = {
    toolbar: translateOrFallback(t('fontDrawer.drawerLabel'), 'Text editor'),
    back: translateOrFallback(t('imageEditDrawer.back'), 'Back'),
    close: translateOrFallback(t('imageEditDrawer.close'), 'Close'),
  };

  const actions = useMemo<ToolbarAction[]>(() => {
    const list: ToolbarAction[] = [];

    // Mobile-only: Text action opens the keyboard / enters edit mode.
    if (isMobile) {
      list.push({
        id: 'text',
        label: translateOrFallback(t('fontDrawer.textTabLabel'), 'Text'),
        icon: IconNames.Keyboard,
        onClick: () => handleTextTabClick(canvas?.current ?? null, true),
      });
    }

    list.push({
      id: 'font-family',
      label: translateOrFallback(t('fontDrawer.FontTabLabel'), 'Font'),
      icon: IconNames.Font,
      onClick: () => openInlineTool('font-family'),
    });

    list.push({
      id: 'font-size',
      label: translateOrFallback(t('fontDrawer.SizeTabLabel'), 'Size'),
      icon: IconNames.Expand,
      onClick: () => openInlineTool('font-size'),
    });

    if (shouldShowColorEditor) {
      list.push({
        id: 'color',
        label: translateOrFallback(t('fontDrawer.ColorTabLabel'), 'Color'),
        icon: IconNames.Paint,
        onClick: () => openInlineTool('color'),
      });
    }

    list.push({
      id: 'alignment',
      label: translateOrFallback(t('fontDrawer.alignmentTabLabel'), 'Align'),
      icon: IconNames.AlignCenterBold,
      onClick: () => openInlineTool('alignment'),
    });

    return list;
  }, [isMobile, shouldShowColorEditor, t, canvas, openInlineTool]);

  const inlineBody = activeTool ? <FontInlineTools tool={activeTool} /> : null;

  if (!isVisible && !isDesktopRendered) {
    return null;
  }

  // In a sub-panel, show the label of the tool being edited (Font / Size /
  // Color / Alignment). In the root state, show the panel-level title.
  // Falls back to "Edit text" when activeTool has no matching action label
  // (e.g. transient values not backed by a menu action).
  const resolvedTitle =
    (activeTool ? actions.find((action) => action.id === activeTool)?.label : undefined) ?? 'Edit text';

  const sharedMenuProps = {
    ariaLabel: labels.toolbar,
    backLabel: labels.back,
    closeLabel: labels.close,
    onBack: handleBack,
    onClose: handleClose,
    showBack: !!activeTool,
    title: resolvedTitle,
    testIdPrefix: TOOLBAR_TEST_ID_PREFIX,
    actions,
  };

  if (isMobile) {
    if (!isVisible) {
      return null;
    }
    return (
      <Drawer
        drawerPosition={DrawerPositions.Bottom}
        isOpen={isVisible}
        onClose={handleClose}
        hideCloseButton
        showVeil={false}
        ariaLabel={labels.toolbar}
        drawerTestId="font-toolbar-mobile"
        bodyClassName={activeTool === 'font-family' ? styles['drawer-body-overflow-visible'] : undefined}
      >
        <ToolbarActionMenu {...sharedMenuProps} variant="embedded" className={styles['panel-mobile']}>
          {inlineBody}
        </ToolbarActionMenu>
      </Drawer>
    );
  }

  const host = getFontToolbarHost();
  if (!host || !isDesktopRendered) {
    return null;
  }

  return createPortal(
    <ToolbarActionMenu
      {...sharedMenuProps}
      variant="floating"
      isClosing={isDesktopClosing}
      onExitComplete={onDesktopExitComplete}
    >
      {inlineBody}
    </ToolbarActionMenu>,
    host,
  );
};
